import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, addDoc, getDocs, where, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaSpinner, FaBell, FaEnvelope, FaEnvelopeOpen, FaChevronDown } from 'react-icons/fa';
import Header from './Header';
import AdminHelpScreen from './AdminHelpScreen';
import ResolveModal from './ResolveModal';

const Help = () => {
  const { currentUser } = useAuth();
  const [userEmail, setUserEmail] = useState('');
  const [username, setUsername] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRequests, setUserRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [isRequestsExpanded, setIsRequestsExpanded] = useState(false);
  const navigate = useNavigate();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  // Subject options for dropdown
  const subjectOptions = [
    { value: '', label: 'Select a subject' },
    { value: 'bug', label: 'I found a bug' },
    { value: 'feature', label: 'Feature suggestion' },
    { value: 'account', label: 'Account issue' },
    { value: 'payment', label: 'Payment problem' },
    { value: 'question', label: 'General question' },
    { value: 'other', label: 'Other' }
  ];

  // Get user data and their support requests on component mount
  useEffect(() => {
    const getUserData = async () => {
      if (currentUser) {
        setUserEmail(currentUser.email || '');
        
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUsername(userData.username || currentUser.displayName || '');
            setIsAdmin(userData.isAdmin || false);
          }
          setLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setLoading(false);
        }
      }
    };

    getUserData();
  }, [currentUser]);

  // Fetch user's support requests
  useEffect(() => {
    if (!currentUser) return;

    const userRequestsQuery = query(
      collection(db, 'supportRequests'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      userRequestsQuery,
      (snapshot) => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        setUserRequests(requests);
      },
      (error) => {
        console.error('Error fetching user requests:', error);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Handle content change and character count
  const handleContentChange = (e) => {
    const value = e.target.value;
    if (value.length <= 1000) {
      setContent(value);
      setCharCount(value.length);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject) {
      toast.error('Please select a subject');
      return;
    }

    if (!content || content.trim().length < 10) {
      toast.error('Please provide more details in your message (minimum 10 characters)');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get subject label for email
      const subjectLabel = subjectOptions.find(option => option.value === subject)?.label || subject;
      
      // Store support request in Firestore
      await addDoc(collection(db, 'supportRequests'), {
        userId: currentUser.uid,
        userEmail,
        username,
        subject: subjectLabel,
        message: content,
        status: 'new',
        createdAt: serverTimestamp(),
        resolved: false
      });

      toast.success('Support request submitted successfully!');
      setContent('');
      setSubject('');
      setCharCount(0);
    } catch (error) {
      console.error('Error sending support request:', error);
      toast.error('Failed to send support request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    // Show modal if there's an admin response, regardless of status
    if (request.adminResponded && request.resolveMessageFromAdmin) {
      setShowResolveModal(true);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    let bgColor, textColor, label;
    
    switch (status) {
      case 'new':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        label = 'New';
        break;
      case 'in-progress':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        label = 'In Progress';
        break;
      case 'resolved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        label = 'Resolved';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        label = status || 'Unknown';
    }
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {label}
      </span>
    );
  };

  // Get pending responses count
  const getPendingResponsesCount = () => {
    return userRequests.filter(req => 
      req.adminResponded && 
      req.resolveMessageFromAdmin && 
      req.approvedByUser === null
    ).length;
  };

  // Add this helper function to count requests by status
  const getRequestStats = () => {
    return {
      new: userRequests.filter(req => req.status === 'new').length,
      inProgress: userRequests.filter(req => req.status === 'in-progress').length,
      resolved: userRequests.filter(req => req.status === 'resolved').length
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <FaSpinner className="animate-spin text-primary-500 h-8 w-8" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {isAdmin ? (
        <div className="max-w-7xl mx-auto">
          <AdminHelpScreen />
        </div>
      ) : (
        <motion.div
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Need Help?
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              We're here to help! Submit a ticket below and our team will get back to you as soon as possible.
            </p>
          </motion.div>

          {/* Show user's existing requests if they have any */}
          {userRequests.length > 0 && (
            <motion.div 
              variants={itemVariants} 
              className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 border-b dark:border-gray-700 cursor-pointer bg-gray-100 dark:bg-gray-800"
                onClick={() => setIsRequestsExpanded(!isRequestsExpanded)}
              >
                {/* Main header with title and chevron */}
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Your Support Requests ({userRequests.length})
                  </h2>
                  <motion.div
                    initial={false}
                    animate={{ rotate: isRequestsExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FaChevronDown className="text-gray-500 flex-shrink-0" />
                  </motion.div>
                </div>

                {/* Status indicators and notifications in a responsive layout */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                  <div className="flex flex-wrap gap-2 text-sm mb-2 sm:mb-0">
                    <span className="flex items-center text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800 pr-2 py-1 rounded-md">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
                      {getRequestStats().new} new
                    </span>
                    <span className="flex items-center text-yellow-600 dark:text-yellow-400 bg-white dark:bg-gray-800 pr-2 py-1 rounded-md">
                      <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></div>
                      {getRequestStats().inProgress} in progress
                    </span>
                    <span className="flex items-center text-green-600 dark:text-green-400 bg-white dark:bg-gray-800 pr-2 py-1 rounded-md">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                      {getRequestStats().resolved} resolved
                    </span>
                  </div>
                  
                  {getPendingResponsesCount() > 0 && (
                    <div className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300 flex items-center self-start sm:self-auto">
                      <FaBell className="mr-1" /> {getPendingResponsesCount()} {getPendingResponsesCount() === 1 ? 'response' : 'responses'} waiting
                    </div>
                  )}
                </div>
              </div>
              
              <motion.div
                initial={false}
                animate={{
                  height: isRequestsExpanded ? 'auto' : 0,
                  opacity: isRequestsExpanded ? 1 : 0
                }}
                transition={{
                  height: { duration: 0.3, ease: "easeInOut" },
                  opacity: { duration: 0.2, ease: "easeInOut" }
                }}
                className="overflow-hidden"
              >
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {userRequests.map((request) => (
                    <motion.li 
                      key={request.id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`hover:bg-gray-50 dark:hover:bg-teal-900 transition-colors duration-150 cursor-pointer ${
                        selectedRequest?.id === request.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                      onClick={() => handleRequestClick(request)}
                    >
                      <div className="p-4">
                        <div className="flex justify-between mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {request.subject}
                          </span>
                          <StatusBadge status={request.status} />
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-500 mb-1">
                          {formatDate(request.createdAt)}
                        </div>
                        <div className="flex items-center text-sm">
                          {request.adminResponded && request.resolveMessageFromAdmin ? (
                            request.approvedByUser === null ? (
                              <div className="text-yellow-600 dark:text-yellow-400 flex items-center">
                                <FaEnvelope className="mr-1" /> Response received - action required
                              </div>
                            ) : (
                              <div className="text-green-600 dark:text-green-400 flex items-center">
                                <FaEnvelopeOpen className="mr-1" /> Response received
                              </div>
                            )
                          ) : (
                            <div className="text-gray-600 dark:text-gray-400">
                              Waiting for response
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          )}

          <motion.div 
            variants={itemVariants} 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
          >
            <div className="p-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Submit a New Request
              </h2>
            </div>
            <div className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* User email - Non-editable */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={userEmail}
                    readOnly
                    className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-300 focus:outline-none cursor-not-allowed"
                  />
                </div>
                
                {/* Username - Non-editable */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    readOnly
                    className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-300 focus:outline-none cursor-not-allowed"
                  />
                </div>
                
                {/* Subject - Dropdown */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject
                  </label>
                  <select
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  >
                    {subjectOptions.map((option) => (
                      <option key={option.value} value={option.value} disabled={option.value === ''}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Content - Textarea */}
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="content"
                    value={content}
                    onChange={handleContentChange}
                    placeholder="Please describe your issue or question in detail..."
                    rows={6}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    required
                  />
                  <div className="mt-1 flex justify-end">
                    <span className={`text-xs ${charCount > 900 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                      {charCount}/1000 characters
                    </span>
                  </div>
                </div>
                
                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex justify-center items-center px-4 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-br from-purple-500 to-teal-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200 ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="mr-2" />
                        Submit Ticket
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
          
          {/* Quick contact info card */}
          <motion.div
            variants={itemVariants}
            className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Other ways to get help
            </h2>
            <div className="text-gray-600 dark:text-gray-400">
              <p className="mb-2">
                <strong>Email:</strong> <a href="mailto:cashsule.app@gmail.com" className="text-purple-600 dark:text-purple-400 hover:underline">cashsule.app@gmail.com</a>
              </p>
              <p>
                Our support team typically responds within 1-2 business days.
              </p>
            </div>
          </motion.div>
          
          {/* Resolve Modal */}
          <ResolveModal 
            isOpen={showResolveModal} 
            onClose={() => setShowResolveModal(false)} 
            supportRequest={selectedRequest} 
          />
        </motion.div>
      )}
    </div>
  );
};

export default Help;
